import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { COLORS } from '../theme';

const COL = '2fr 1.5fr 1fr 0.75fr 1fr 1fr 1fr 1.5fr';
const HEADERS = ['Name', 'Ambassador ID', 'Status', 'AI Rec', 'Total Referred', 'Qualified', 'Rewards Pending', 'Approved'];
const STATUS_FILTERS = ['all', 'approved', 'pending', 'suspended', 'denied'];

function Skeleton() {
  return (
    <div style={{
      height: '14px',
      backgroundColor: COLORS.surfaceHover,
      borderRadius: '4px',
      animation: 'pulse 1.4s ease-in-out infinite',
      width: '75%',
    }} />
  );
}

function AmbStatusBadge({ status }) {
  const map = {
    approved:  { bg: '#34A85326', text: COLORS.success,        label: 'Approved' },
    pending:   { bg: '#F5A62326', text: COLORS.warning,        label: 'Pending' },
    suspended: { bg: '#EA433526', text: COLORS.danger,         label: 'Suspended' },
    denied:    { bg: '#EA433526', text: COLORS.danger,         label: 'Denied' },
  };
  const s = map[status] || { bg: COLORS.surfaceHover, text: COLORS.textSecondary, label: status || '—' };
  return (
    <span style={{
      backgroundColor: s.bg,
      color: s.text,
      borderRadius: '4px',
      padding: '2px 8px',
      fontSize: '11px',
      fontWeight: '600',
    }}>
      {s.label}
    </span>
  );
}

function AiRecBadge({ recommendation }) {
  const map = {
    approve: { bg: '#4CAF50', label: 'Approve' },
    review:  { bg: '#FF9800', label: 'Review' },
    deny:    { bg: '#f44336', label: 'Deny' },
  };
  const s = map[recommendation];
  if (!s) return <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>—</span>;
  return (
    <span style={{
      backgroundColor: s.bg, color: 'white',
      borderRadius: '4px', padding: '2px 6px',
      fontSize: '11px', fontWeight: '600',
    }}>
      {s.label}
    </span>
  );
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(amount) {
  const n = Number(amount) || 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function AmbassadorsPage() {
  const navigate = useNavigate();
  const [ambassadors, setAmbassadors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const unsub = onSnapshot(
      query(
        collection(db, 'users'),
        where('ambassador.status', 'in', ['pending', 'approved', 'suspended', 'denied'])
      ),
      snap => {
        const sorted = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const aTime = a.ambassador?.approvedAt?.toMillis?.()
              || a.ambassador?.appliedAt?.toMillis?.()
              || a.createdAt?.toMillis?.() || 0;
            const bTime = b.ambassador?.approvedAt?.toMillis?.()
              || b.ambassador?.appliedAt?.toMillis?.()
              || b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
          });
        setAmbassadors(sorted);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = statusFilter === 'all'
    ? ambassadors
    : ambassadors.filter(a => a.ambassador?.status === statusFilter);

  const headerCell = {
    fontSize: '12px',
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '0 0 12px 0',
    fontWeight: '600',
  };
  const cell = { padding: '12px 0', fontSize: '13px', color: COLORS.textPrimary };

  return (
    <>
      <style>{`@keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>

      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {STATUS_FILTERS.map(f => {
            const active = statusFilter === f;
            return (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: active ? '600' : '400',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: active ? `1px solid ${COLORS.accent}` : `1px solid ${COLORS.border}`,
                  backgroundColor: active ? COLORS.accent + '26' : 'transparent',
                  color: active ? COLORS.accent : COLORS.textSecondary,
                  textTransform: 'capitalize',
                }}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {loading ? '…' : `${filtered.length} ambassador${filtered.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: COL,
          padding: '0 20px',
          borderBottom: `1px solid ${COLORS.border}`,
        }}>
          {HEADERS.map(h => (
            <div key={h} style={headerCell}>{h}</div>
          ))}
        </div>

        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: COL,
              padding: '14px 20px',
              borderBottom: `1px solid ${COLORS.border}30`,
            }}>
              {Array.from({ length: 8 }).map((__, j) => (
                <div key={j}><Skeleton /></div>
              ))}
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
            {statusFilter === 'all' ? 'No ambassadors found.' : `No ${statusFilter} ambassadors.`}
          </div>
        ) : (
          filtered.map((a, i) => {
            const amb = a.ambassador || {};
            const name = a.displayName || [a.firstName, a.lastName].filter(Boolean).join(' ') || a.email || 'Unknown';
            return (
              <div
                key={a.id}
                onClick={() => navigate(`/ambassadors/${a.id}`)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: COL,
                  padding: '0 20px',
                  borderBottom: i < filtered.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: 'background-color 0.1s',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = COLORS.surfaceHover}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={cell}>
                  <div style={{ fontWeight: '500' }}>{name}</div>
                  <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '2px' }}>{a.email || ''}</div>
                </div>
                <div style={{ ...cell, color: amb.ambassadorId ? COLORS.textPrimary : COLORS.textMuted, fontSize: '12px', fontFamily: 'monospace' }}>
                  {amb.ambassadorId || '—'}
                </div>
                <div style={cell}>
                  <AmbStatusBadge status={amb.status} />
                </div>
                <div style={cell}>
                  <AiRecBadge recommendation={amb.aiRecommendation} />
                </div>
                <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '13px' }}>
                  {amb.totalReferred ?? 0}
                </div>
                <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '13px' }}>
                  {amb.qualifiedReferred ?? 0}
                </div>
                <div style={{ ...cell, color: amb.rewardsPending ? COLORS.success : COLORS.textMuted, fontSize: '13px', fontWeight: amb.rewardsPending ? '500' : '400' }}>
                  {fmtCurrency(amb.rewardsPending)}
                </div>
                <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '12px' }}>
                  {fmtDate(amb.approvedAt)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
